import { useEffect, useState, type AnchorHTMLAttributes, type MouseEvent } from "react";
import { handOverToShell } from "./shell-routing";
import { withSurfaceBase } from "./surface-base";

export interface LocationState {
  pathname: string;
  search: string;
}

export function useLocation(): LocationState {
  const [location, setLocation] = useState(readLocation);
  useEffect(() => {
    const update = () => setLocation(readLocation());
    window.addEventListener("popstate", update);
    window.addEventListener("wvcea:navigate", update);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener("wvcea:navigate", update);
    };
  }, []);
  return location;
}

export function Link({ href, onClick, fromRoot = false, ...props }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; fromRoot?: boolean }) {
  // Links are written from the surface's own root. On a host that serves one surface that
  // is already right and this changes nothing; where one host serves several, it puts the
  // prefix back so the link stays inside the app the reader is actually in.
  //
  // fromRoot marks the exceptions: an address written from the host's root, meant to leave
  // this surface. "Return to the convention guide" means the guide, and prefixing it would
  // send a reader who asked to leave back to the staff home page instead.
  const target = fromRoot ? href : withSurfaceBase(href);
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    // A link into another tab's section is handed to the shell, which opens it in the tab
    // that owns it and leaves this one alone. Nothing below runs: this page is staying
    // exactly where it is, which is the whole point — tap More afterwards and the room
    // directory she came from is still on screen. Returns false in a browser, in the
    // installed web app, and for any link that stays inside this tab.
    if (handOverToShell(target, window.location.pathname)) return;
    window.history.pushState(null, "", target);
    window.dispatchEvent(new Event("wvcea:navigate"));
    // "instant", not "auto": auto defers to the CSS scroll-behavior, which is
    // smooth. An animated reset is still running when React swaps the page, the
    // document gets shorter, and the browser clamps the scroll to the new
    // maximum — so a tap from far down the schedule opened a session at its
    // bottom. Instant lands at the top before the swap.
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  return <a href={target} onClick={handleClick} {...props} />;
}

export function replaceSearch(params: URLSearchParams): void {
  const search = params.toString();
  window.history.replaceState(null, "", `${window.location.pathname}${search ? `?${search}` : ""}`);
}

function readLocation(): LocationState {
  return { pathname: window.location.pathname, search: window.location.search };
}
